import { TNavItem, TNavSection } from "../types/navItem";
import { orgIsInLaunchDarkly } from "./launchDarklyUtils";

/** Nav capabilities that AI Scribe–only orgs should not see. */
const AI_SCRIBE_ONLY_HIDDEN_CAPABILITIES = new Set([
  "feature_clinical_tools",
  "feature_clinical_insights",
  "feature_clinical_encounters",
  "feature_clinical_care_plans",
  "feature_clinical_prescriptions",
  "feature_clinical_triage",
  "feature_clinical_med_admin",
  "feature_detailed_encounters",
  "feature_appointments",
  // Facility admin (locations/services). Practice Info stays available to all.
  "feature_facility_mgmt",
]);

/** Paths hidden for AI Scribe–only even when a shared capability might apply. */
const AI_SCRIBE_ONLY_HIDDEN_PATHS = new Set(["/facility"]);

const CLINICAL_CORE_LD_FLAGS = new Set([
  "medicalEncountersFeature",
  "carePlanFeature",
  "prescriptionFeature",
  "allowTriageDashboard",
  "medicationAdministrationFeature",
  "healthInformationSummaryFeature",
]);

function rolesMatch(
  allowedRoles: string[] | undefined,
  userRoles: string[],
): boolean {
  if (allowedRoles) {
    return allowedRoles.some((role) => userRoles?.includes(role));
  }
  return true;
}

function resolveTier(
  adminSettings: { tier?: string } | null | undefined,
  entitlementTier?: string,
): string {
  return (entitlementTier || adminSettings?.tier || "").toUpperCase();
}

/**
 * True for AI Scribe–only access: AI_SCRIBE tier, or ai_scribe without clinical tools/encounters.
 */
export function isAiScribeOnlyNav(
  can: (action: string) => boolean,
  adminSettings?: { tier?: string } | null,
  entitlementTier?: string,
): boolean {
  const tier = resolveTier(adminSettings, entitlementTier);
  if (tier === "AI_SCRIBE") {
    return true;
  }
  return (
    can("ai_scribe") &&
    !can("feature_clinical_tools") &&
    !can("feature_clinical_encounters")
  );
}

export function shouldRenderMenuItem(
  item: TNavItem,
  launchDarklyFlags: Record<string, unknown>,
  orgId: string,
  userRoles: string[],
  can: (action: string) => boolean, // 'can' is now mandatory
  authId?: string,
  entitlementTier?: string,
  adminSettings?: { tier?: string } | null,
): boolean {
  try {
    const { ldFlag, ldOrgFlag, allowedRoles, capability, path } = item;
    const scribeOnly = isAiScribeOnlyNav(can, adminSettings, entitlementTier);

    const billingEnabledByLd = orgIsInLaunchDarkly(
      launchDarklyFlags.billingEnabled,
      orgId,
      authId,
    );
    const billingEnabledByEntitlement =
      capability === "billing_access" || ldOrgFlag === "billingEnabled"
        ? can("billing_access")
        : false;

    // AI Scribe–only: hide clinical chart / tools / facility-admin nav.
    if (scribeOnly && path && AI_SCRIBE_ONLY_HIDDEN_PATHS.has(path)) {
      return false;
    }
    if (
      scribeOnly &&
      capability &&
      AI_SCRIBE_ONLY_HIDDEN_CAPABILITIES.has(capability) &&
      // Practice Info shares feature_facility_mgmt with Facility admin but
      // must remain available for org details editing.
      path !== "/practice-info"
    ) {
      return false;
    }

    // --- Entitlement-First Logic ---
    // 1. Primary Gate: Check capability from our entitlements system
    // Billing: entitlement or LD billingEnabled is enough (role still applies).
    if (capability) {
      const allowed = can(capability);
      if (
        !allowed &&
        !(capability === "billing_access" && billingEnabledByLd)
      ) {
        return false;
      }
    }

    // 2. Role Match: Ensure user has the required roles
    if (!rolesMatch(allowedRoles, userRoles)) {
      return false;
    }

    // --- LaunchDarkly Logic (as a secondary check) ---
    const tier = resolveTier(adminSettings, entitlementTier);
    const isOnboardedTier = [
      "PREMIUM",
      "ENTERPRISE",
      "ESSENTIALS",
      "TRIAL",
      "BASIC",
      "AI_SCRIBE",
    ].includes(tier);

    const orgMatches = (flagValue: unknown): boolean => {
      if (!flagValue) return true; // If flag is not set, don't block

      // OVERRIDE: If the flag is medicalRecordEnabled, allow it automatically for recognized tiers
      if (ldOrgFlag === "medicalRecordEnabled" && isOnboardedTier) {
        return true;
      }

      return orgIsInLaunchDarkly(flagValue, orgId, authId);
    };

    // 3. LD Flags: If capability check passed, check LD flags
    // If ldFlag is present, it must be true.
    if (ldFlag && !launchDarklyFlags[ldFlag]) {
      // OVERRIDE: For onboarded tiers, we bypass the LD kill-switch for these core features
      const isCoreFeature = [
        "appointmentsFeature",
        "billingFeature",
        "broadcastFeature",
        "healthInformationSummaryFeature",
        "medicalEncountersFeature",
        "carePlanFeature",
        "prescriptionFeature",
      ].includes(ldFlag);
      // AI Scribe–only must not inherit the clinical core-feature LD bypass.
      if (
        !isOnboardedTier ||
        !isCoreFeature ||
        (scribeOnly && CLINICAL_CORE_LD_FLAGS.has(ldFlag))
      ) {
        return false;
      }
    }

    // If ldOrgFlag is present, the org must be in the list.
    // Billing: a billing_access entitlement can stand in for billingEnabled.
    if (ldOrgFlag && !orgMatches(launchDarklyFlags[ldOrgFlag])) {
      if (!(ldOrgFlag === "billingEnabled" && billingEnabledByEntitlement)) {
        return false;
      }
    }

    // If all checks pass, render the item
    return true;
  } catch (e) {
    console.error("Error in shouldRenderMenuItem:", e);
    return false;
  }
}

export function filterNavSections(
  sections: TNavSection[],
  launchDarklyFlags: Record<string, unknown>,
  orgId: string,
  userRoles: string[],
  can: (action: string) => boolean,
  authId?: string,
  entitlementTier?: string,
  adminSettings?: { tier?: string } | null,
): TNavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        shouldRenderMenuItem(
          item,
          launchDarklyFlags,
          orgId,
          userRoles,
          can,
          authId,
          entitlementTier,
          adminSettings,
        ),
      ),
    }))
    .filter(
      (section) =>
        shouldRenderMenuItem(
          section,
          launchDarklyFlags,
          orgId,
          userRoles,
          can,
          authId,
          entitlementTier,
          adminSettings,
        ) && section.items.length > 0,
    );
}
